import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { getMoviesByCategory } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

const baseUrl = 'https://qiqicinema.com';

export default function CategoryPage() {
  const { category = '全部' } = useParams<{ category: string }>();
  const [activeCategory, setActiveCategory] = useState(category);

  useEffect(() => {
    setActiveCategory(category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category]);

  const displayMovies = getMoviesByCategory(activeCategory);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `琪琪影院 - ${activeCategory}分类`,
        url: `${baseUrl}/category/${activeCategory}`,
        isPartOf: { '@type': 'WebSite', name: '琪琪影院', url: baseUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${baseUrl}/category/全部` },
          { '@type': 'ListItem', position: 3, name: activeCategory },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`琪琪影院${activeCategory} - 高清${activeCategory}在线观看 | 蓝光画质流畅播放`}
        description={`琪琪影院${activeCategory}频道提供海量${activeCategory}资源，蓝光画质不缓冲播放，流畅观影体验，超清视频免费在线观看。`}
        keywords={`${activeCategory},高清${activeCategory},${activeCategory}在线观看,免费${activeCategory},琪琪影院`}
        canonicalUrl={`${baseUrl}/category/${activeCategory}`}
        ogType="website"
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        {/* Hero */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/全部' },
                { label: activeCategory },
              ]}
            />
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mt-2">
              {activeCategory === '全部' ? '全部影视资源' : activeCategory}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共找到 <strong className="text-primary-500">{displayMovies.length}</strong> 部{activeCategory === '全部' ? '影视' : activeCategory}资源
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200 sticky top-16 z-40">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.name}`}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.name
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            {displayMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
                {displayMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-movie-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}